import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { Iipd } from '../../ipd/Iipd.type';
import { Iopd } from '../opddetails/iopddetails';
import { IPatientInfo } from '../../patient/ipatient-info';
import { DoctorInfo } from '../../user/iusers';
import { VitalDetailsValue } from './vital.type';

@Component({
  selector: 'app-vital-details',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  templateUrl: './vital-details.component.html',
  styleUrl: './vital-details.component.scss'
})
export class VitalDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @HostBinding('class.vital-details-standalone')
  get standaloneLayout(): boolean {
    return this.showActions && !this.isDialog;
  }

  @Input() patientDetails?: IPatientInfo | null;
  @Input() opdDetails?: Iopd | null;
  @Input() ipdData?: Iipd | null;
  @Input() doctorData?: DoctorInfo | null;
  @Input() readOnly = false;
  @Input() isDialog = false;
  @Input() showActions = false;
  @Input() initialVitals?: VitalDetailsValue | null;

  @Output() vitalsChanged = new EventEmitter<VitalDetailsValue>();
  @Output() saveVitals = new EventEmitter<VitalDetailsValue>();

  vitalForm!: FormGroup;
  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
    this.patchVitals();
    this.applyReadOnlyState();

    this.vitalForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.vitalsChanged.emit(this.getVitalsValue()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.vitalForm) {
      return;
    }

    if (changes['patientDetails'] || changes['initialVitals']) {
      this.patchVitals();
    }

    if (changes['readOnly']) {
      this.applyReadOnlyState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canEdit(): boolean {
    return !this.readOnly;
  }

  get invalid(): boolean {
    return this.vitalForm?.invalid ?? false;
  }

  markAllAsTouched(): void {
    this.vitalForm?.markAllAsTouched();
  }

  onSaveVitals(): void {
    if (this.vitalForm?.invalid) {
      this.vitalForm.markAllAsTouched();
      return;
    }

    this.saveVitals.emit(this.getVitalsValue());
  }

  private initializeForm(): void {
    this.vitalForm = this.fb.group({
      bloodPressure: ['', Validators.maxLength(50)],
      weight: ['', Validators.min(0)],
      heartRate: ['', Validators.min(0)],
      oxygenSaturation: ['', [Validators.min(0), Validators.max(100)]],
      bodyTemperature: ['', Validators.min(0)]
    });
  }

  private patchVitals(): void {
    const values: VitalDetailsValue = {
      bloodPressure: this.initialVitals?.bloodPressure ?? '',
      weight: this.initialVitals?.weight ?? '',
      heartRate: this.initialVitals?.heartRate ?? '',
      oxygenSaturation: this.initialVitals?.oxygenSaturation ?? '',
      bodyTemperature: this.initialVitals?.bodyTemperature ?? '',
    };

    this.vitalForm.patchValue(values, { emitEvent: false });
  }

  private applyReadOnlyState(): void {
    if (this.readOnly) {
      this.vitalForm.disable({ emitEvent: false });
      return;
    }

    this.vitalForm.enable({ emitEvent: false });
  }

  private getVitalsValue(): VitalDetailsValue {
    const values = this.vitalForm.getRawValue();
    return {
      bloodPressure: values.bloodPressure ?? '',
      weight: values.weight ?? '',
      heartRate: values.heartRate ?? '',
      oxygenSaturation: values.oxygenSaturation ?? '',
      bodyTemperature: values.bodyTemperature ?? '',
    };
  }

}
